import React from 'react'

/** Matches paragraph references such as "[[1.2 > A.]]" embedded in finding descriptions. */
const PARAGRAPH_REF_PATTERN = /\[\[(.+?)\]\]/g

/** Matches the " > " breadcrumb separator within a paragraph reference. */
const BREADCRUMB_SEPARATOR_PATTERN = / > /g

/** Nicer chevron glyph used in place of the raw ">" breadcrumb separator. */
const BREADCRUMB_SEPARATOR_GLYPH = ' › '

/** Refs shorter than this stay inline with surrounding text instead of forcing their own line — avoids choppy short lines. */
const MIN_REF_LENGTH_FOR_OWN_LINE = 20

interface DescriptionSegment {
    part: string
    isRef: boolean
}

/** Groups description segments into lines: a long ref gets its own line, short refs stay inline with adjacent text. */
const groupDescriptionIntoLines = (segments: DescriptionSegment[]): DescriptionSegment[][] => {
    const lines: DescriptionSegment[][] = []
    let currentLine: DescriptionSegment[] = []
    segments.forEach((segment) => {
        if (segment.isRef && segment.part.length >= MIN_REF_LENGTH_FOR_OWN_LINE) {
            if (currentLine.length) lines.push(currentLine)
            lines.push([segment])
            currentLine = []
        } else {
            currentLine.push(segment)
        }
    })
    if (currentLine.length) lines.push(currentLine)
    return lines
}

/** Strips "[[ ]]" markers from paragraph references and applies the same breadcrumb glyph substitution used when rendering them. */
export const stripParagraphRefs = (description: string): string =>
    description.replace(PARAGRAPH_REF_PATTERN, (_match, inner: string) =>
        inner.replace(BREADCRUMB_SEPARATOR_PATTERN, BREADCRUMB_SEPARATOR_GLYPH)
    )

/** Splits a description on paragraph-reference tokens, rendering each line (long ref, or a run of text/short refs) as its own block. */
export const renderDescriptionWithParagraphRefs = (
    description: string,
    lineClassName: string,
    highlightClassName: string
): React.ReactNode[] => {
    const segments = description
        .split(PARAGRAPH_REF_PATTERN)
        .map((part, index) => ({
            part: index % 2 === 1 ? part.replace(BREADCRUMB_SEPARATOR_PATTERN, BREADCRUMB_SEPARATOR_GLYPH) : part,
            isRef: index % 2 === 1,
        }))
        .filter(({ part }) => part !== '')

    return groupDescriptionIntoLines(segments).map((line, lineIndex) => (
        <span key={lineIndex} className={lineClassName}>
            {line.map(({ part, isRef }, index) =>
                isRef ? (
                    <span key={index} className={highlightClassName}>
                        {part}
                    </span>
                ) : (
                    part
                )
            )}
        </span>
    ))
}
